import React, { useState, useEffect, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { PencilIcon } from '@heroicons/react/24/outline';
import { FaCamera, FaUserCircle } from 'react-icons/fa'; // Import icons for consistency

const AddCategoryDialog = ({ isOpen, onClose, onSubmit, initialData = null, isEditing = false }) => {
  const [categoryName, setCategoryName] = useState('');
  const [id, setId] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [base64Image, setBase64Image] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef(null);

  // Initialize form fields when dialog opens with initialData (for editing)
  useEffect(() => {
    if (initialData && isOpen && isEditing) {
      // Set category name from initialData
      setCategoryName(initialData.name || '');
      setId(initialData._id || '');
      // Set image preview if there's an existing imageUrl
      if (initialData.imageUrl) {
        setImagePreview(initialData.imageUrl);

        // Convert existing imageUrl to base64
        if (initialData.imageUrl) {
          fetchImageAsBase64(initialData.imageUrl);
        }
      } else {
        setImagePreview('');
      }
    } else if (!isOpen) {
      // Reset form when dialog closes
      setCategoryName('');
      setImageFile(null);
      setImagePreview('');
      setBase64Image('');
      setId('');
    }
  }, [initialData, isOpen]);

  // Function to fetch and convert image URL to base64
  const fetchImageAsBase64 = async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();

      const reader = new FileReader();
      reader.onloadend = () => {
        // Get just the base64 data without the prefix
        const base64String = reader.result.split(',')[1];
        setBase64Image(base64String);
      };
      reader.readAsDataURL(blob);
    } catch (error) {
      console.error('Error converting image to base64:', error);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Add validation like in GoogleCalendarPanel
    if (!file.type.match('image.*')) {
      console.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      console.error('Image size should be less than 5MB');
      return;
    }

    setImageFile(file);

    // Create preview URL and convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
      // Extract base64 part without the prefix
      const base64String = reader.result.split(',')[1];
      setBase64Image(base64String);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!categoryName.trim() || isLoading) return;

    setIsLoading(true); // Set loading state to true when submission starts

    let imageUrl = initialData?.imageUrl || ''; // Start with existing imageUrl when editing

    try {
      // Pass category ID if editing an existing category, otherwise use the generated one
      await onSubmit({
        _id: initialData?._id, // Use existing ID when editing
        image: imageFile,
        name: categoryName.trim(),
        imageUrl, // Include the image URL
        base64Url: base64Image, // Include the base64 converted URL
        acctId: localStorage.getItem('acctId')
      });

      // Reset form
      setCategoryName('');
      setImageFile(null);
      setImagePreview('');
      setBase64Image('');
      setId('');
    } catch (error) {
      console.error('Error submitting category:', error);
    } finally {
      setIsLoading(false); // Reset loading state regardless of outcome
    }
  };

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        onClose={() => { }} // Empty function to prevent auto-closing
        static // Add static prop to prevent closing on outside click
      >
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-4">
                <div className="relative p-0">
                  <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-1 right-1 p-1 rounded-full bg-black hover:bg-gray-800 transition-colors duration-200 text-white hover:text-gray-200 z-10"
                    aria-label="Close"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
                
                {/* Header with border-bottom */}
                <div className="border-b border-gray-200 py-4 mb-8">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    {isEditing ? 'Edit Category' : 'Add New Category'}
                  </Dialog.Title>
                </div>

                {/* Category Name Input */}
                <div className="mt-8 px-4">
                  <div className="flex items-center space-x-4">
                    <label
                      htmlFor="categoryName"
                      className="block text-sm font-medium text-gray-900 w-1/3"
                    >
                      Category Name
                    </label>
                    <div className="flex-grow">
                      <input
                        id="categoryName"
                        name="categoryName"
                        type="text"
                        placeholder="Enter category name"
                        value={categoryName}
                        onChange={(e) => setCategoryName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && categoryName.trim()) {
                            handleSubmit();
                          }
                        }}
                        className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-black text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Image Upload section with gray default "C" character and fixed dimensions */}
                <div className="mt-6 px-4">
                  <div className="flex items-center space-x-4">
                    <label
                      htmlFor="categoryImage"
                      className="block text-sm font-medium text-gray-900 w-1/3"
                    >
                      Category Image
                    </label>
                    <div className="flex-grow">
                      <div
                        className="relative group cursor-pointer inline-block"
                        onClick={() => fileInputRef.current.click()}
                      >
                        {imagePreview ? (
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="w-24 h-16 rounded-lg object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              console.log("Error loading image, using default avatar");
                              // Create gray circle with "C" initial for category
                              e.target.src = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Ccircle cx='50' cy='50' r='50' fill='%23767676'/%3E%3Ctext x='50' y='50' font-family='Arial' font-size='40' fill='white' text-anchor='middle' dominant-baseline='central'%3EC%3C/text%3E%3C/svg%3E`;
                            }}
                          />
                        ) : (
                          // Gray default image with "C" character, fixed dimensions
                          <div className="w-24 h-16 rounded-lg bg-gray-500 flex items-center justify-center">
                            <span className="text-white text-2xl font-bold">C</span>
                          </div>
                        )}

                        {/* Camera icon overlay on hover - contained within image boundaries */}
                        <div className="absolute inset-0 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black bg-opacity-50">
                          <FaCamera className="text-white w-6 h-6" />
                        </div>

                        {/* Hidden file input */}
                        <input
                          ref={fileInputRef}
                          type="file"
                          id="categoryImage"
                          name="categoryImage"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="hidden"
                        />
                      </div>

                    </div>
                  </div>
                </div>

                {/* Button Section with top border */}
                <div className="border-t border-gray-200 mt-10">
                  <div className="flex justify-between mt-5">
                    <button
                      type="button"
                      onClick={onClose}
                      disabled={isLoading}
                      className={`rounded-md bg-gray-200 text-gray-700 py-2 px-4 ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-300'
                        } focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2`}
                    >
                      Close
                    </button>
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={!categoryName.trim() || isLoading}
                      className={`rounded-md ${!categoryName.trim() || isLoading ? "bg-gray-400 cursor-not-allowed" : "bg-black hover:bg-gray-800"
                        } text-white py-2 px-4 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 min-w-[100px]`}
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          {isEditing ? 'Saving...' : 'Creating...'}
                        </div>
                      ) : (
                        isEditing ? 'Save' : 'Create'
                      )}
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default AddCategoryDialog;